import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Search, X, Users, Handshake, Briefcase, FileText, Calendar, ScrollText } from "lucide-react";

const GROUP_META = {
  clienti:   { label: "Clienti e imprese", icon: Users },
  partner:   { label: "Partner", icon: Handshake },
  pratiche:  { label: "Pratiche", icon: Briefcase },
  bandi:     { label: "Bandi e strumenti", icon: ScrollText },
  documenti: { label: "Documenti", icon: FileText },
  eventi:    { label: "Eventi agenda", icon: Calendar },
};

function highlight(text, q) {
  if (!text || !q) return text || "";
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return text;
  return <>{text.slice(0, idx)}<mark style={{background:"#FFE082", color:"#8A0000", padding:"0 2px", borderRadius:2}}>{text.slice(idx, idx+q.length)}</mark>{text.slice(idx+q.length)}</>;
}

export default function GlobalSearch({ open, onClose }) {
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [focusIdx, setFocusIdx] = useState(0);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    else { setQ(""); setData(null); }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setData(null); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/search", { params: { q: q.trim(), include_archived: includeArchived } });
        setData(r.data); setFocusIdx(0);
      } catch {} finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, includeArchived, open]);

  // Flatten to a linear list for keyboard nav
  const flat = data ? Object.entries(data.groups || {}).flatMap(([g, items]) => items.map(it => ({ ...it, _group: g }))) : [];

  const go = useCallback((item) => {
    if (!item?.url) return;
    nav(item.url); onClose();
  }, [nav, onClose]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") { onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx(i => Math.min(flat.length - 1, i + 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setFocusIdx(i => Math.max(0, i - 1)); }
    if (e.key === "Enter") { e.preventDefault(); if (flat[focusIdx]) go(flat[focusIdx]); }
  };

  if (!open) return null;
  return (
    <div className="ma-modal-overlay" onClick={onClose} style={{alignItems:"flex-start", paddingTop:80}} data-testid="global-search-modal">
      <div className="ma-modal" onClick={e=>e.stopPropagation()} style={{maxWidth:720, width:"100%"}}>
        <div style={{display:"flex", alignItems:"center", padding:"14px 20px", borderBottom:"1px solid var(--ma-border)", gap:10}}>
          <Search size={18} color="var(--ma-text-muted)"/>
          <input ref={inputRef} value={q} onChange={e=>setQ(e.target.value)} onKeyDown={onKeyDown}
                 placeholder="Cerca clienti, partner, pratiche, bandi, documenti, eventi..."
                 data-testid="global-search-input"
                 style={{flex:1, border:"none", outline:"none", fontSize:15, background:"transparent", color:"var(--ma-text)"}}/>
          <label className="ma-checkbox" style={{fontSize:12}}>
            <input type="checkbox" checked={includeArchived} onChange={e=>setIncludeArchived(e.target.checked)}/> Archiviati
          </label>
          <button className="ma-btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        <div style={{maxHeight:"60vh", overflowY:"auto", padding:"8px 0"}}>
          {loading && <div className="ma-loading">Ricerca in corso…</div>}
          {!loading && q.trim().length < 2 && (
            <div className="ma-empty">Digita almeno 2 caratteri per iniziare. Usa ↑ ↓ per navigare, Invio per aprire.</div>
          )}
          {!loading && data && data.total === 0 && (
            <div className="ma-empty">Nessun risultato per "{q}".</div>
          )}
          {!loading && data && data.total > 0 && Object.entries(data.groups).filter(([_,v]) => v.length > 0).map(([g, items]) => {
            const meta = GROUP_META[g] || { label: g, icon: Search };
            const Icon = meta.icon;
            return (
              <div key={g} style={{marginBottom:8}}>
                <div style={{padding:"6px 20px", color:"var(--ma-text-muted)", fontSize:11, textTransform:"uppercase", letterSpacing:1, fontWeight:700,
                              display:"flex", alignItems:"center", gap:6}}>
                  <Icon size={13}/> {meta.label} ({items.length})
                </div>
                {items.map(it => {
                  const idxInFlat = flat.indexOf(it);
                  const focused = idxInFlat === focusIdx;
                  return (
                    <div key={g+"-"+it.id} onClick={()=>go(it)}
                          data-testid={`gs-result-${g}-${it.id}`}
                          style={{padding:"10px 20px", cursor:"pointer",
                                  background: focused ? "var(--ma-hover)" : "transparent",
                                  borderLeft: focused ? "3px solid var(--ma-red)" : "3px solid transparent"}}>
                      <ResultRow group={g} item={it} q={q}/>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ResultRow({ group, item, q }) {
  if (group === "clienti") return (
    <div>
      <div style={{fontWeight:600}}>{highlight(item.display_name, q)}
        {item.archived && <span className="ma-pill pill-gray" style={{marginLeft:8}}>Archiviato</span>}</div>
      <div className="ma-sub-xs">{item.subject_type==="person"?"Persona":"Impresa"} · {item.fiscal_code||item.vat_number||item.email||"—"}</div>
    </div>
  );
  if (group === "partner") return (
    <div>
      <div style={{fontWeight:600}}>{highlight(item.name, q)}
        {item.archived && <span className="ma-pill pill-gray" style={{marginLeft:8}}>Archiviato</span>}</div>
      <div className="ma-sub-xs">{item.code} · {item.partner_type} · {item.email||"—"}</div>
    </div>
  );
  if (group === "pratiche") return (
    <div>
      <div style={{fontWeight:600}}><span className="ma-code-badge">{item.code}</span> {highlight(item.title, q)}
        {item.archived && <span className="ma-pill pill-gray" style={{marginLeft:8}}>Archiviata</span>}</div>
      <div className="ma-sub-xs">{item.subject_name||"—"}</div>
    </div>
  );
  if (group === "bandi") return (
    <div>
      <div style={{fontWeight:600}}><span className="ma-code-badge">{item.code}</span> {highlight(item.title, q)}
        {item.archived && <span className="ma-pill pill-gray" style={{marginLeft:8}}>Archiviato</span>}</div>
      <div className="ma-sub-xs">{item.tool_type} · {item.status}</div>
    </div>
  );
  if (group === "documenti") return (
    <div>
      <div style={{fontWeight:600}}>{highlight(item.name, q)}</div>
      <div className="ma-sub-xs">Documento</div>
    </div>
  );
  if (group === "eventi") return (
    <div>
      <div style={{fontWeight:600}}>{highlight(item.title, q)}</div>
      <div className="ma-sub-xs">{item.start_at ? new Date(item.start_at).toLocaleString("it-IT") : "—"} · {item.category}</div>
    </div>
  );
  return <div>{JSON.stringify(item)}</div>;
}
